import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import SuccessNotification from '../../components/ui/SuccessNotification';
import AccountSecurity from './components/AccountSecurity';
import PrivacyControls from './components/PrivacyControls';
import NotificationManagement from './components/NotificationManagement';
import ProfileEditor from './components/ProfileEditor';
import RoleSpecificSettings from './components/RoleSpecificSettings';
import AccessibilitySettings from './components/AccessibilitySettings';

const UniversalAccountSettingsHub = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [activeSection, setActiveSection] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const showSuccessNotification = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-4xl">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-8 text-center">
            <Icon name="Lock" size={48} className="text-blue-200 mx-auto mb-4" />
            <h2 className="text-xl font-heading font-semibold text-white mb-2">
              Access Restricted
            </h2>
            <p className="text-blue-100 mb-6">
              Please log in to access account settings.
            </p>
          </div>
        </main>
      </div>
    );
  }

  const sections = [
    { id: 'profile', label: 'Profile', icon: 'User' },
    { id: 'security', label: 'Security', icon: 'Shield' },
    { id: 'privacy', label: 'Privacy', icon: 'Lock' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'role-specific', label: 'Preferences', icon: 'Settings' },
    { id: 'accessibility', label: 'Accessibility', icon: 'Eye' }
  ];

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'profile':
        return <ProfileEditor user={user} userProfile={userProfile} onSuccess={showSuccessNotification} />;
      case 'security':
        return <AccountSecurity user={user} onSuccess={showSuccessNotification} />;
      case 'privacy':
        return <PrivacyControls user={user} userProfile={userProfile} onSuccess={showSuccessNotification} />;
      case 'notifications':
        return <NotificationManagement user={user} userProfile={userProfile} onSuccess={showSuccessNotification} />;
      case 'role-specific':
        return <RoleSpecificSettings user={user} userProfile={userProfile} onSuccess={showSuccessNotification} />;
      case 'accessibility':
        return <AccessibilitySettings user={user} userProfile={userProfile} onSuccess={showSuccessNotification} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Account Settings - RoleCall</title>
        <meta name="description" content="Manage your account settings, preferences, and profile" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-7xl">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-xl text-blue-200 hover:text-white hover:bg-white hover:bg-opacity-10 transition-colors duration-200"
                aria-label="Go back"
              >
                <Icon name="ArrowLeft" size={24} />
              </button>
              <div>
                <h1 className="text-3xl font-heading font-bold text-white">
                  Account Settings
                </h1>
                <p className="text-blue-100 font-body mt-1">
                  Manage your profile, security, and preferences
                </p>
              </div>
            </div>
          </div>

          {/* Success Notification */}
          {showSuccess && (
            <SuccessNotification
              message={successMessage}
              isVisible={showSuccess}
              onClose={() => setShowSuccess(false)}
            />
          )}

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar Navigation */}
            <div className="lg:col-span-1">
              <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-4 space-y-2">
                {sections?.map((section) => (
                  <button
                    key={section?.id}
                    onClick={() => setActiveSection(section?.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                      activeSection === section?.id
                        ? 'bg-white bg-opacity-20 text-white shadow-lg'
                        : 'text-blue-100 hover:bg-white hover:bg-opacity-10 hover:text-white'
                    }`}
                  >
                    <Icon name={section?.icon} size={20} />
                    <span className="font-medium">{section?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Content Area */}
            <div className="lg:col-span-3">
              {loading ? (
                <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-12 flex items-center justify-center">
                  <LoadingSpinner />
                </div>
              ) : (
                renderActiveSection()
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default UniversalAccountSettingsHub;